import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Not part of the normal client/doctor/graphologist auth system — gated by a
// shared secret typed in below, checked against ADMIN_SECRET on the backend.
// Not linked anywhere in the app; reached only by navigating here directly.
const AdminDoctors = () => {
  const [secret, setSecret] = useState(sessionStorage.getItem('admin_secret') || '');
  const [unlocked, setUnlocked] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [checking, setChecking] = useState(false);

  const [specOptions, setSpecOptions] = useState([]);
  const [langOptions, setLangOptions] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({}); // per-doctor edit state, keyed by id
  const [saving, setSaving] = useState(null); // doctor id currently saving

  const authHeaders = useCallback(() => ({ headers: { 'x-admin-secret': secret } }), [secret]);

  const loadData = useCallback(async (currentSecret) => {
    setLoading(true);
    try {
      const [specRes, langRes, docRes] = await Promise.all([
        axios.get('/admin/specialisations', { headers: { 'x-admin-secret': currentSecret } }),
        axios.get('/admin/languages', { headers: { 'x-admin-secret': currentSecret } }),
        axios.get('/admin/doctors', { headers: { 'x-admin-secret': currentSecret } })
      ]);
      setSpecOptions(specRes.data.specialisations || []);
      setLangOptions(langRes.data.languages || []);
      setDoctors(docRes.data.doctors || []);

      const initialDrafts = {};
      for (const d of docRes.data.doctors || []) {
        initialDrafts[d.id] = {
          bio: d.bio || '',
          experience: d.experience || '',
          sessions: d.sessions || '',
          specialisationIds: (d.specialisations || []).map(s => s.id),
          languageIds: (d.languages || []).map(l => l.id)
        };
      }
      setDrafts(initialDrafts);
    } catch (err) {
      toast.error('Could not load doctors.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setChecking(true);
    setUnlockError('');
    try {
      // Any admin GET works as a check; specialisations is the smallest.
      await axios.get('/admin/specialisations', { headers: { 'x-admin-secret': secret } });
      sessionStorage.setItem('admin_secret', secret);
      setUnlocked(true);
      loadData(secret);
    } catch (err) {
      setUnlockError('Incorrect secret.');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // If a secret is already in this tab's session, skip the prompt.
    if (secret) {
      handleUnlock({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraft = (doctorId, field, value) => {
    setDrafts(prev => ({ ...prev, [doctorId]: { ...prev[doctorId], [field]: value } }));
  };

  const toggleSpecialisation = (doctorId, specId) => {
    setDrafts(prev => {
      const current = prev[doctorId].specialisationIds;
      const next = current.includes(specId)
        ? current.filter(id => id !== specId)
        : [...current, specId];
      return { ...prev, [doctorId]: { ...prev[doctorId], specialisationIds: next } };
    });
  };

  const toggleLanguage = (doctorId, langId) => {
    setDrafts(prev => {
      const current = prev[doctorId].languageIds;
      const next = current.includes(langId)
        ? current.filter(id => id !== langId)
        : [...current, langId];
      return { ...prev, [doctorId]: { ...prev[doctorId], languageIds: next } };
    });
  };

  const saveDoctor = async (doctorId) => {
    const draft = drafts[doctorId];
    setSaving(doctorId);
    try {
      await axios.put(
        `/admin/doctors/${doctorId}/profile`,
        { bio: draft.bio, experience: draft.experience, sessions: draft.sessions },
        authHeaders()
      );
      await axios.put(
        `/admin/doctors/${doctorId}/specialisations`,
        { specialisationIds: draft.specialisationIds },
        authHeaders()
      );
      await axios.put(
        `/admin/doctors/${doctorId}/languages`,
        { languageIds: draft.languageIds },
        authHeaders()
      );
      toast.success('Saved.');
      loadData(secret);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save changes.');
    } finally {
      setSaving(null);
    }
  };

  if (!unlocked) {
    return (
      <div style={styles.gate}>
        <ToastContainer position="top-center" />
        <form onSubmit={handleUnlock} style={styles.gateForm}>
          <h3 style={{ marginTop: 0 }}>Admin access</h3>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Admin secret"
            style={styles.input}
            autoFocus
          />
          {unlockError && <p style={{ color: '#c0392b', fontSize: 13 }}>{unlockError}</p>}
          <button type="submit" disabled={checking || !secret} style={styles.primaryBtn}>
            {checking ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" />
      <h2 style={styles.pageTitle}>Therapist Specialisations & Languages</h2>
      <p style={styles.meta}>
        Editing bio, experience, sessions, specialisations, and languages for {doctors.length} therapist{doctors.length === 1 ? '' : 's'}.
      </p>

      {loading ? (
        <p>Loading…</p>
      ) : (
        doctors.map((doc) => {
          const draft = drafts[doc.id] || { bio: '', experience: '', sessions: '', specialisationIds: [], languageIds: [] };
          const isExpanded = expandedId === doc.id;

          return (
            <div key={doc.id} style={styles.card}>
              <div
                style={styles.cardHeader}
                onClick={() => setExpandedId(isExpanded ? null : doc.id)}
              >
                <div>
                  <strong>{doc.name}</strong>
                  <span style={styles.metaInline}>
                    {' '}— {(doc.specialisations || []).map(s => s.name).join(', ') || 'No specialisations set'}
                    {' · '}{(doc.languages || []).map(l => l.name).join(', ') || 'No languages set'}
                  </span>
                </div>
                <span>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {isExpanded && (
                <div style={styles.cardBody}>
                  <label style={styles.label}>Bio</label>
                  <textarea
                    value={draft.bio}
                    onChange={(e) => updateDraft(doc.id, 'bio', e.target.value)}
                    style={styles.textarea}
                    rows={3}
                  />

                  <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Experience (e.g. "8 yrs")</label>
                      <input
                        value={draft.experience}
                        onChange={(e) => updateDraft(doc.id, 'experience', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Sessions (e.g. "240+")</label>
                      <input
                        value={draft.sessions}
                        onChange={(e) => updateDraft(doc.id, 'sessions', e.target.value)}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <label style={styles.label}>Specialisations</label>
                  <div style={styles.checkGrid}>
                    {specOptions.map((spec) => (
                      <label key={spec.id} style={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={draft.specialisationIds.includes(spec.id)}
                          onChange={() => toggleSpecialisation(doc.id, spec.id)}
                        />
                        {spec.name}
                      </label>
                    ))}
                  </div>

                  <label style={styles.label}>Languages spoken</label>
                  <div style={styles.checkGrid}>
                    {langOptions.map((lang) => (
                      <label key={lang.id} style={styles.checkLabel}>
                        <input
                          type="checkbox"
                          checked={draft.languageIds.includes(lang.id)}
                          onChange={() => toggleLanguage(doc.id, lang.id)}
                        />
                        {lang.name}
                      </label>
                    ))}
                  </div>

                  <button
                    onClick={() => saveDoctor(doc.id)}
                    disabled={saving === doc.id}
                    style={{ ...styles.primaryBtn, marginTop: 16 }}
                  >
                    {saving === doc.id ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const styles = {
  gate: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  gateForm: { background: 'white', padding: 32, borderRadius: 16, border: '1px solid rgba(0,0,0,0.08)', width: 320 },
  page: { maxWidth: 900, margin: '40px auto', padding: '0 24px' },
  pageTitle: { fontFamily: "'DM Serif Display', serif", color: '#1a2344', margin: '0 0 4px' },
  meta: { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  metaInline: { color: '#6b7280', fontWeight: 400, fontSize: 14 },
  card: { border: '1px solid rgba(48,60,108,0.1)', borderRadius: 12, marginBottom: 12, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', cursor: 'pointer', background: '#fafbfc' },
  cardBody: { padding: 18, borderTop: '1px solid rgba(48,60,108,0.06)' },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 6px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginTop: 6 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 },
  primaryBtn: { padding: '10px 20px', borderRadius: 50, border: 'none', background: '#1a2344', color: 'white', fontWeight: 600, fontSize: 14, cursor: 'pointer' }
};

export default AdminDoctors;